/*
 * Infinite scroll for the collection product grid.
 *
 * Renders as <infinite-scroll> inside #ProductGridContainer, immediately after
 * the product grid and the numbered pagination.
 *
 * Design notes:
 *  - It lives INSIDE #ProductGridContainer on purpose. When facets.js applies a
 *    filter it replaces that container's innerHTML wholesale, which destroys the
 *    old element and upgrades a fresh one. connectedCallback then re-initialises
 *    against the new paginate state automatically. No coupling to facets.js.
 *  - Pages are fetched through the Section Rendering API (?section_id=), so the
 *    response is just this section rather than the whole document.
 *  - The numbered pagination stays in the DOM and is only hidden visually once
 *    this script is running, so crawlers and no-JS visitors keep working links.
 */

class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById('product-grid');
    this.sectionId = this.dataset.sectionId;
    this.nextUrl = this.dataset.nextUrl || '';
    this.loading = false;
    this.failures = 0;

    // Nothing to page through, or the grid is missing entirely.
    if (!this.grid || !this.nextUrl) {
      this.hideSpinner();
      return;
    }

    this.hidePagination();

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.loadNextPage();
        });
      },
      // Start fetching before the sentinel is actually on screen so the append
      // usually lands before the visitor reaches the bottom.
      { rootMargin: '800px 0px 800px 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  hidePagination() {
    document
      .querySelectorAll('#ProductGridContainer .pagination-wrapper')
      .forEach((el) => el.classList.add('pagination-wrapper--infinite'));
  }

  showPagination() {
    document
      .querySelectorAll('#ProductGridContainer .pagination-wrapper')
      .forEach((el) => el.classList.remove('pagination-wrapper--infinite'));
  }

  hideSpinner() {
    this.classList.add('infinite-scroll--done');
  }

  buildUrl(nextUrl) {
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  async loadNextPage() {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    this.classList.add('infinite-scroll--loading');

    try {
      const response = await fetch(this.buildUrl(this.nextUrl));
      if (!response.ok) throw new Error(`Status ${response.status}`);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const incomingGrid = doc.getElementById('product-grid');
      const incomingSentinel = doc.querySelector('infinite-scroll');

      if (!incomingGrid) throw new Error('No product grid in response');

      this.appendItems(incomingGrid);

      // Advance to the following page, or stop if this was the last one.
      const followingUrl = incomingSentinel ? incomingSentinel.dataset.nextUrl : null;
      if (followingUrl) {
        this.nextUrl = followingUrl;
        this.dataset.nextUrl = followingUrl;
      } else {
        this.nextUrl = '';
        delete this.dataset.nextUrl;
        this.observer.disconnect();
        this.hideSpinner();
      }

      this.failures = 0;
    } catch (error) {
      console.error('[infinite-scroll]', error);
      this.failures += 1;

      // Two consecutive failures: stop trying and hand the visitor back the
      // numbered links rather than stranding them at the bottom of the page.
      if (this.failures >= 2) {
        this.observer.disconnect();
        this.hideSpinner();
        this.showPagination();
      }
    } finally {
      this.loading = false;
      this.classList.remove('infinite-scroll--loading');

      // The sentinel may still be inside the viewport after appending (short
      // pages, tall screens). Re-observing re-fires the callback if so.
      if (this.nextUrl && this.observer) {
        this.observer.unobserve(this);
        this.observer.observe(this);
      }
    }
  }

  appendItems(incomingGrid) {
    const items = Array.from(incomingGrid.children);
    if (!items.length) return;

    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      // Dawn's reveal-on-scroll observer only watches elements present at load,
      // so appended cards would stay at opacity 0 forever. Strip the animation
      // hooks and let them render immediately.
      item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
      item.removeAttribute('data-cascade');
      item.style.removeProperty('--animation-order');
      fragment.appendChild(item);
    });

    this.grid.appendChild(fragment);

    // Re-apply the ready-to-ship size filter to the cards that just arrived.
    if (typeof window.filterVariantsByPreferredSize === 'function') {
      window.filterVariantsByPreferredSize();
    }

    // Let apps (wishlist, reviews, currency) re-bind to the new cards.
    document.dispatchEvent(
      new CustomEvent('infinite-scroll:appended', {
        bubbles: true,
        detail: { items, grid: this.grid },
      })
    );
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}